import { Box, Progress, Table as RT, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react';
import { flexRender, Table as ReactTable, Row } from '@tanstack/react-table';
import React from 'react';

import { TableFilterStatus } from './TableFilterStatus';
import { TableHeaderCellContent } from './TableHeaderCellContent';

type TableProps<T> = {
    table: ReactTable<T>;
    onRowClick?: (data: T) => void;
    allowXOverflow?: boolean;
    shrinkToContent?: boolean;
    isLoading?: boolean;
    hiddenFilterIds?: string[];
    SubComponent?: React.FC<{ row: Row<T> }>;
};

export const Table = <T,>({
    table,
    onRowClick,
    allowXOverflow,
    shrinkToContent,
    isLoading,
    hiddenFilterIds,
    SubComponent,
}: TableProps<T>) => {
    return (
        <Box w="100%">
            <TableFilterStatus table={table} hiddenFilterIds={hiddenFilterIds} />
            <Box overflowX={allowXOverflow ? 'auto' : 'initial'} pb={2}>
                <RT size="sm" width={shrinkToContent ? 'max-content' : undefined}>
                    <Thead>
                        {table.getHeaderGroups().map((headerGroup) => (
                            <Tr key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Th
                                        key={header.id}
                                        whiteSpace={allowXOverflow ? 'nowrap' : 'initial'}
                                        wordBreak={allowXOverflow ? 'keep-all' : 'initial'}
                                        borderColor="gray.600"
                                        paddingY={1}
                                        paddingX={3}
                                    >
                                        <TableHeaderCellContent header={header} table={table} />
                                    </Th>
                                ))}
                            </Tr>
                        ))}
                    </Thead>
                    <Tbody>
                        {table.getRowModel().rows.map((row) => (
                            <React.Fragment key={row.id}>
                                <Tr
                                    _hover={{
                                        background: 'whiteAlpha.100',
                                        cursor: onRowClick ? 'pointer' : 'default',
                                    }}
                                    onClick={() => onRowClick?.(row.original)}
                                >
                                    {row.getVisibleCells().map((cell) => {
                                        return (
                                            <Td
                                                key={cell.id}
                                                fontSize="sm"
                                                paddingY={1}
                                                paddingX={2}
                                                whiteSpace={allowXOverflow ? 'nowrap' : 'initial'}
                                                wordBreak={allowXOverflow ? 'keep-all' : 'initial'}
                                                borderColor="gray.600"
                                                position={cell.column.columnDef.meta?.isSticky ? 'sticky' : undefined}
                                                left={cell.column.columnDef.meta?.isSticky ? 0 : undefined}
                                                background={
                                                    cell.column.columnDef.meta?.isSticky ? 'gray.700' : undefined
                                                }
                                                width={`${cell.column.getSize()}px`}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </Td>
                                        );
                                    })}
                                </Tr>

                                {row.getCanExpand() && row.getIsExpanded() && SubComponent && (
                                    <Tr backgroundColor="gray.800">
                                        <Td colSpan={row.getVisibleCells().length}>
                                            <SubComponent row={row} />
                                        </Td>
                                    </Tr>
                                )}
                            </React.Fragment>
                        ))}
                    </Tbody>
                </RT>
                {isLoading ? <Progress colorScheme="teal" size="xs" isIndeterminate /> : <Box h={1} />}
            </Box>
        </Box>
    );
};
